// src/components/Modals/ModalProgramacion/ModalMatchOfficials.jsx
/**
 * PURPOSE: Modal compacto de Match Officials para un partido puntual de Programación.
 * RESPONSIBILITIES: Cargar roles/asignaciones vigentes del partido, permitir guardado parcial y
 *   alternar DRAFT/OFFICIAL de forma explícita (nunca automática al editar).
 * COLLABORATORS: matchOfficialsService.js, ModalProgramacion.jsx.
 * ROUTE: winscore/src/components/Modals/ModalProgramacion/ModalMatchOfficials.jsx
 */
import React, { useEffect, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import { toast } from "react-hot-toast";

import {
  getApiErrorMessage,
  getMatchOfficialsDesignation,
  lookupOfficialForQuickCreate,
  quickCreateAndAssignOfficial,
  saveMatchOfficialsAssignments,
  setMatchOfficialsStatus,
} from "../../../services/matchOfficialsService.js";

const EMPTY_QUICK_CREATE_FORM = { documentNumber: "", firstname: "", lastname: "" };

const ModalMatchOfficials = ({ open, setOpen, match }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [designation, setDesignation] = useState(null);
  const [selection, setSelection] = useState({});

  // Quick-create/enable-and-assign (runtime finding 2): compact "+" per role row, scoped to that
  // one role only — never the full Officials Master UI.
  const [quickCreateOpenFor, setQuickCreateOpenFor] = useState(null);
  const [quickCreateForm, setQuickCreateForm] = useState(EMPTY_QUICK_CREATE_FORM);
  const [quickCreateLookup, setQuickCreateLookup] = useState(null);
  const [quickCreateLooking, setQuickCreateLooking] = useState(false);
  const [quickCreateSubmitting, setQuickCreateSubmitting] = useState(false);

  const applyDesignation = (data) => {
    setDesignation(data);
    const nextSelection = {};
    (data?.roles || []).forEach((role) => {
      nextSelection[role.idrole] = role.assignedOfficial?.idofficial || "";
    });
    setSelection(nextSelection);
  };

  const loadDesignation = async () => {
    setLoading(true);
    try {
      applyDesignation(await getMatchOfficialsDesignation(match.idmatch));
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudieron cargar los oficiales del partido."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && match?.idmatch) {
      loadDesignation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, match?.idmatch]);

  const handleSelectChange = (idrole, value) => {
    setSelection((current) => ({ ...current, [idrole]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const assignments = (designation?.roles || []).map((role) => ({
        idrole: role.idrole,
        idofficial: selection[role.idrole] ? Number(selection[role.idrole]) : null,
      }));
      const updated = await saveMatchOfficialsAssignments(match.idmatch, assignments);
      setDesignation(updated);
      toast.success("Oficiales del partido guardados correctamente.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudieron guardar los oficiales."));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    const nextStatus = designation?.status === "OFFICIAL" ? "DRAFT" : "OFFICIAL";
    setChangingStatus(true);
    try {
      const updated = await setMatchOfficialsStatus(match.idmatch, nextStatus);
      setDesignation(updated);
      toast.success(
        nextStatus === "OFFICIAL" ? "Designación marcada como Oficial." : "Designación devuelta a Borrador."
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo cambiar el estado de la designación."));
    } finally {
      setChangingStatus(false);
    }
  };

  const isOfficial = designation?.status === "OFFICIAL";
  const busy = saving || changingStatus || quickCreateSubmitting;
  // FTR-0004: sourced from the designation response's additive `sportFamily` field so quick-create
  // copy can name the match's sport without the frontend re-deriving sport family itself.
  const sportFamilyName = designation?.sportFamily?.name || "la disciplina del partido";

  const openQuickCreate = (idrole) => {
    setQuickCreateOpenFor(idrole);
    setQuickCreateForm(EMPTY_QUICK_CREATE_FORM);
    setQuickCreateLookup(null);
  };

  const closeQuickCreate = () => {
    setQuickCreateOpenFor(null);
    setQuickCreateForm(EMPTY_QUICK_CREATE_FORM);
    setQuickCreateLookup(null);
  };

  const handleQuickCreateFormChange = (event) => {
    const { name, value } = event.target;
    setQuickCreateForm((current) => ({ ...current, [name]: value }));
    // Any edit invalidates the previous lookup result so a stale CTA can't be confirmed.
    setQuickCreateLookup(null);
  };

  const handleQuickCreateLookup = async (idrole) => {
    const documentNumber = quickCreateForm.documentNumber.trim();
    if (!documentNumber) {
      toast.error("Ingresa el número de documento.");
      return;
    }
    setQuickCreateLooking(true);
    try {
      setQuickCreateLookup(await lookupOfficialForQuickCreate(match.idmatch, idrole, documentNumber));
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo buscar el oficial."));
    } finally {
      setQuickCreateLooking(false);
    }
  };

  const handleQuickCreateConfirm = async (idrole) => {
    if (quickCreateLookup?.outcome === "NEW_OFFICIAL") {
      if (!quickCreateForm.firstname.trim() || !quickCreateForm.lastname.trim()) {
        toast.error("Nombres y apellidos son obligatorios para crear un nuevo oficial.");
        return;
      }
    }
    setQuickCreateSubmitting(true);
    try {
      const result = await quickCreateAndAssignOfficial(match.idmatch, idrole, {
        documentNumber: quickCreateForm.documentNumber.trim(),
        firstname: quickCreateForm.firstname.trim(),
        lastname: quickCreateForm.lastname.trim(),
      });
      applyDesignation(result.designation);
      toast.success("Oficial asignado correctamente.");
      closeQuickCreate();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo crear/asignar al oficial."));
    } finally {
      setQuickCreateSubmitting(false);
    }
  };

  return (
    <Modal
      aria-labelledby="match-officials-modal-title"
      open={open}
      onClose={() => setOpen(false)}
      className="flex items-center justify-center"
    >
      <Sheet
        variant="outlined"
        className="w-full max-w-2xl rounded-md bg-white p-6 shadow-lg"
      >
        <ModalClose variant="plain" className="m-1" onClick={() => setOpen(false)} />
        <Typography
          component="h2"
          id="match-officials-modal-title"
          level="h4"
          className="mb-1 text-2xl font-semibold"
        >
          Oficiales del Partido
        </Typography>
        <p className="mb-4 text-sm text-gray-500">
          {match?.Participant1?.Institution?.abrev || "Pendiente"} vs{" "}
          {match?.Participant2?.Institution?.abrev || "Pendiente"}
        </p>

        {loading ? (
          <div className="py-8 text-center text-sm text-gray-500">Cargando...</div>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
              <span className="text-sm font-medium text-gray-700">
                Estado:{" "}
                <span
                  className={`ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                    isOfficial ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {isOfficial ? "Oficial" : "Borrador"}
                </span>
              </span>
              <button
                type="button"
                onClick={handleToggleStatus}
                disabled={busy}
                className="rounded-md border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
              >
                {changingStatus
                  ? "Actualizando..."
                  : isOfficial
                  ? "Volver a Borrador"
                  : "Marcar como Oficial"}
              </button>
            </div>

            <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
              {(designation?.roles || []).map((role) => {
                const currentOfficialId = role.assignedOfficial?.idofficial || null;
                const currentIsEligible = role.eligibleOfficials.some(
                  (official) => official.idofficial === currentOfficialId
                );
                const quickCreateOpen = quickCreateOpenFor === role.idrole;
                const outcome = quickCreateOpen ? quickCreateLookup?.outcome : null;
                return (
                  <div key={role.idrole}>
                    <div className="grid grid-cols-1 gap-1 sm:grid-cols-3 sm:items-center">
                      <label className="text-sm font-medium text-gray-700 sm:col-span-1">{role.name}</label>
                      <div className="flex items-center gap-2 sm:col-span-2">
                        <select
                          className="w-full rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                          value={selection[role.idrole] || ""}
                          onChange={(event) => handleSelectChange(role.idrole, event.target.value)}
                        >
                          <option value="">Sin asignar</option>
                          {currentOfficialId && !currentIsEligible && (
                            <option value={currentOfficialId}>
                              {role.assignedOfficial.fullName} (inactivo)
                            </option>
                          )}
                          {role.eligibleOfficials.map((official) => (
                            <option key={official.idofficial} value={official.idofficial}>
                              {official.fullName}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          title="Crear oficial para este rol"
                          aria-label="Crear oficial para este rol"
                          disabled={busy}
                          onClick={() => (quickCreateOpen ? closeQuickCreate() : openQuickCreate(role.idrole))}
                          className={`shrink-0 rounded-md border px-2.5 py-2 text-sm font-bold leading-none disabled:opacity-60 ${
                            quickCreateOpen
                              ? "border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200"
                              : "border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                          }`}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {quickCreateOpen && (
                      <div className="mt-2 rounded-md border border-indigo-100 bg-indigo-50/60 p-3 sm:ml-[calc(33.333%+0.25rem)]">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                          Crear oficial para este rol
                        </p>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                          <input
                            name="documentNumber"
                            value={quickCreateForm.documentNumber}
                            onChange={handleQuickCreateFormChange}
                            placeholder="Documento"
                            className="rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                          />
                          <input
                            name="firstname"
                            value={quickCreateForm.firstname}
                            onChange={handleQuickCreateFormChange}
                            placeholder="Nombres"
                            className="rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                          />
                          <input
                            name="lastname"
                            value={quickCreateForm.lastname}
                            onChange={handleQuickCreateFormChange}
                            placeholder="Apellidos"
                            className="rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                          />
                        </div>

                        {!outcome ? (
                          <div className="mt-2 flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={closeQuickCreate}
                              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleQuickCreateLookup(role.idrole)}
                              disabled={quickCreateLooking}
                              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                            >
                              {quickCreateLooking ? "Buscando..." : "Buscar"}
                            </button>
                          </div>
                        ) : outcome === "EXISTING_INACTIVE" ? (
                          <div className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            <strong>{quickCreateLookup.official?.fullName}</strong> ya existe pero está{" "}
                            <strong>inactivo</strong>. Debe reactivarse desde el Master de Oficiales (rol 1) antes de
                            poder asignarse.
                            <div className="mt-2 flex justify-end">
                              <button
                                type="button"
                                onClick={closeQuickCreate}
                                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                              >
                                Cerrar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2">
                            {outcome !== "NEW_OFFICIAL" && (
                              <p className="mb-2 text-xs text-gray-600">
                                Oficial existente: <strong>{quickCreateLookup.official?.fullName}</strong>
                              </p>
                            )}
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={closeQuickCreate}
                                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                              >
                                Cancelar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleQuickCreateConfirm(role.idrole)}
                                disabled={quickCreateSubmitting}
                                className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                              >
                                {quickCreateSubmitting
                                  ? "Guardando..."
                                  : outcome === "NEW_OFFICIAL"
                                  ? "Crear y asignar"
                                  : outcome === "EXISTING_ELIGIBLE"
                                  ? "Asignar oficial existente"
                                  : `Habilitar para ${sportFamilyName} y asignar`}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {(designation?.roles || []).length === 0 && (
                <p className="text-sm text-gray-500">
                  No hay roles configurados para la disciplina de este partido.
                </p>
              )}
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </>
        )}
      </Sheet>
    </Modal>
  );
};

export default ModalMatchOfficials;
